<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Globe } from 'lucide-vue-next'
import { getLocale, setLocale, getSupportedLocales, type SupportedLocale } from '@/locales/i18n'

const isOpen = ref(false)
const dropdownRef = ref<HTMLDivElement | null>(null)

const currentLocale = computed(() => {
  const locale = getLocale()
  return getSupportedLocales().find(l => l.code === locale) || getSupportedLocales()[0]
})

const locales = getSupportedLocales()

function selectLocale(code: SupportedLocale) {
  console.log('[i18n] User selected locale:', code)
  setLocale(code)
  isOpen.value = false
}

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div ref="dropdownRef" class="relative">
    <button
      @click.stop="isOpen = !isOpen"
      class="flex items-center gap-1.5 px-2 py-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
      :aria-label="'Language: ' + currentLocale.name"
    >
      <Globe :size="18" />
      <span class="text-sm font-medium">{{ currentLocale.flag }}</span>
    </button>

    <!-- Dropdown -->
    <Transition name="dropdown">
      <div
        v-if="isOpen"
        class="absolute top-full right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[140px] z-50"
      >
        <button
          v-for="locale in locales"
          :key="locale.code"
          @click="selectLocale(locale.code)"
          :class="[
            'w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors',
            locale.code === currentLocale.code
              ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          ]"
        >
          <span>{{ locale.flag }}</span>
          <span>{{ locale.name }}</span>
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
